// @flow
import type { DictionaryType } from "../types/GeneralTypes";

export const bannerStyles = (theme: DictionaryType): DictionaryType => ({
  banner: {
    width: "calc(100% - 32px)",
    height: "auto",
    margin: "16px",
    "&$error": {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white
    },
    "&$warning": {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.common.white
    },
    "&$info": {
      backgroundColor: theme.palette.grey.light,
      color: theme.palette.common.black
    }
  },
  error: {},
  warning: {},
  info: {},
  box: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    justifyContent: "center",
    transition: "color 0.25s ease-in-out",
    whiteSpace: "pre-wrap",
    padding: "16px",
    "& $content": {
      color: "inherit",
      fontSize: "0.9375rem",
      fontWeight: "500",
      lineHeight: "1.5em"
    }
  },
  content: {}
});

export const pannelStyles = (theme: DictionaryType): DictionaryType => ({
  appWrapper: {
    display: "block",
    margin: "0 auto",
    width: "100%"
  },
  appMain: {
    width: "100%",
    height: "100%"
  },
  appPanel: {
    minHeight: "400px",
    background: theme.palette.common.white,
    border: "none",
    clear: "both",
    display: "block",
    margin: "24px 0 0",
    padding: 0,
    position: "relative",
    width: "100%"
  },
  appPanelContent: {
    display: "block",
    width: "100%",
    height: "auto"
  },
  appPanelTop: {
    marginTop: "25px"
  },
  "@media screen and (max-width:959px)": {
    appMain: {
      marginTop: "65px"
    }
  }
});

export const tableStyles = (theme: DictionaryType): DictionaryType => ({
  appTable: {
    overflow: "auto",
    width: "100%",
    "& tr": {
      "&$active": {
        background: "#e0e0e0",
        color: "rgba(0,0,0,.87)"
      }
    },
    "&$noBorder": {
      border: "none",
      "& tr, & th, & td": {
        border: "none"
      }
    },
    "&$lightBorder": {
      border: "1px solid " + theme.palette.grey.light,
      "& tr, & th, & td": {
        border: "1px solid " + theme.palette.grey.light
      }
    },
    "&$center": {
      "& th, & td": {
        textAlign: "center"
      }
    },
    "&$colorTop": {
      borderTop: ".2em solid " + theme.palette.primary.light
    },
    "& $border": {
      fontSize: "0.875rem",
      fontWeight: 600
    },
    "& $normal": {
      fontSize: "0.875rem"
    },
    "& $compact": {
      padding: "5px"
    },
    "& $short": {
      paddingLeft: "5px"
    },
    "& $slim": {
      height: "32px"
    },
    "& $top": {
      verticalAlign: "top"
    },
    "& $light": {
      color: theme.palette.grey.light
    },
    "& $inlineDivider": {
      borderTop: "1px solid " + theme.palette.primary.light,
      height: "24px"
    },
    "& $blueShine": {
      color: theme.palette.blue.main,
      fontSize: "0.9375rem"
    }
  },
  noBorder: {},
  lightBorder: {},
  center: {},
  colorTop: {},
  active: {},
  border: {},
  normal: {},
  compact: {},
  short: {},
  slim: {},
  top: {},
  light: {},
  inlineDivider: {},
  blueShine: {},
  "@media screen and (max-width:959px)": {
    appTable: {
      "& tr": {
        width: "auto !important",
        display: "block !important",
        height: "auto !important"
      },
      "& th, & td": {
        width: "auto !important",
        display: "block !important",
        minHeight: "30px"
      }
    }
  }
});

const ToolBarHeight = 80;
const DrawWidth = 240;

export const drawerStyles = (theme: DictionaryType): DictionaryType => ({
  root: {
    flexGrow: 1,
    height: "100vh",
    zIndex: 1,
    overflow: "auto",
    position: "relative",
    display: "flex"
  },
  appBar: {
    top: "0",
    left: "auto",
    right: "0",
    position: "absolute",
    zIndex: 1600,
    width: "100%",
    height: ToolBarHeight,
    display: "flex",
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  appBarShift: {
    marginLeft: DrawWidth,
    width: `calc(100% - ${DrawWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  appBarHeader: {
    width: "100%"
  },
  menuButton: {
    marginLeft: 12,
    marginRight: 12
  },
  hide: {
    display: "none"
  },
  hidden: {
    opacity: 0
  },
  drawerPaper: {
    position: "relative",
    width: DrawWidth,
    zIndex: 1400,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    width: theme.spacing.unit * 8
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    height: ToolBarHeight,
    position: "fixed",
    width: "inherit",
    borderBottom: "1px solid " + theme.palette.grey.light,
    zIndex: 1200
  },
  content: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing.unit * 3,
    marginTop: ToolBarHeight
  },
  wrap: {
    position: "absolute",
    left: DrawWidth + theme.spacing.unit * 3,
    right: theme.spacing.unit * 3,
    top: ToolBarHeight,
    bottom: 0,
    padding: theme.spacing.unit * 1,
    width: "inherit",
    overflowY: "auto",
    overflowX: "hidden"
  },
  wrapClose: {
    left: theme.spacing.unit * 11
  },
  item: {},
  itemSelected: {
    backgroundColor: theme.palette.grey.light
  },
  itemHidden: {
    paddingLeft: "12px"
  },
  itemTitle: {
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  itemTitleSelected: {
    fontWeight: 800
  },
  list: {
    marginTop: ToolBarHeight,
    zIndex: 1000,
    overflowY: "auto",
    overflowX: "hidden"
  }
});

export const headerStyles = (theme: DictionaryType): DictionaryType => ({
  appHeader: {
    borderBottom: "1px solid " + theme.palette.primary.main,
    padding: "24px 10px"
  },
  headerWrapper: {
    height: "32px",
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between"
  },
  headerTitle: {
    fontSize: "1.5rem",
    fontStyle: "inherit",
    fontWeight: "500",
    letterSpacing: "-0.01em",
    lineHeight: "32px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  actionItem: {
    flex: "1 0 auto"
  }
});

export const colorLabel = {
  borderRadius: "2em",
  display: "inline-block",
  padding: "2px 12px"
};

export const redLabel = {
  ...colorLabel,
  backgroundColor: "#FF5630",
  color: "#FFFFFF"
};

export const pinkLabel = {
  ...colorLabel,
  backgroundColor: "#FFEBE6",
  color: "#BF2600"
};

export const greenLabel = {
  ...colorLabel,
  backgroundColor: "#39b54a",
  color: "#FFFFFF"
};

export const blueLabel = {
  ...colorLabel,
  backgroundColor: "#23BEBE",
  color: "#FFFFFF"
};

export const darkLabel = {
  ...colorLabel,
  backgroundColor: "#286d32",
  color: "#FFFFFF"
};

export const greyLabel = {
  ...colorLabel,
  backgroundColor: "#8a8a8a",
  color: "#FFFFFF"
};

export const formText = {
  width: "100%",
  textAlign: "center"
};

export const topBanner = {
  position: "fixed",
  top: "5%",
  left: "10%",
  width: "80%",
  zIndex: 99999999
};

export const padImg = {
  width: "35px",
  height: "35px"
};

export const formRightBtnBlock = {
  width: "auto",
  display: "flex",
  justifyContent: "flex-end",
  margin: "0 2% 0 2%"
};
